import React, { useState, useContext } from "react";
import { UserContext } from "../context/UserContext";
import {
  Container,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Paper,
  Grid,
  Box,
} from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import DescriptionIcon from "@mui/icons-material/Description";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PlaceIcon from "@mui/icons-material/Place";
import PublicIcon from "@mui/icons-material/Public";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CategoryIcon from "@mui/icons-material/Category";
import axios from "axios"; // Import axios
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../../src/config";

const PostClassified = () => {
  const { user } = useContext(UserContext);
  const [newClassified, setNewClassified] = useState({
    title: "",
    description: "",
    price: "",
    city: "", // Only city now, previously location was used
    category: "",
    state: "",
    country: "",
    contact_number: "",
    email: "",
    images: [], // To hold comma-separated image URLs
  });

  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const categories = [
    "Electronics",
    "Vehicles",
    "Jobs",
    "Real Estate",
    "Services",
    "Others",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewClassified({ ...newClassified, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    let tempErrors = {};

    // Title validation
    if (!newClassified.title) tempErrors.title = "Title is required";
    
    // Price validation
    if (!newClassified.price) tempErrors.price = "Price is required";
    if (isNaN(newClassified.price) || Number(newClassified.price) <= 0)
      tempErrors.price = "Enter a valid price";
    
    // City validation
    if (!newClassified.city) tempErrors.city = "City is required";

    // Category validation
    if (!newClassified.category) tempErrors.category = "Category is required";

    // Contact number validation
    const phoneRegex = /^[0-9]{10}$/; // Validate for 10-digit numbers
    if (newClassified.contact_number && !phoneRegex.test(newClassified.contact_number)) {
      tempErrors.contact_number = "Enter a valid contact number (10 digits)";
    }

    // Email validation
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (newClassified.email && !emailRegex.test(newClassified.email)) {
      tempErrors.email = "Enter a valid email address";
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handlePost = async () => {
    if (!validateForm()) return;

    const imageArray = Array.isArray(newClassified.images)
      ? newClassified.images.map((image) => image.url.trim())
      : [];

    const postData = {
      ...newClassified,
      images: imageArray,
      user_id: user?.id, // Add user ID here
    };

    try {
      const response = await axios.post(
        `${API_BASE_URL}/classifieds`,
        postData
      );
      console.log("Classified posted successfully:", response.data);
      alert("Classified is created successfully");
    } catch (error) {
      console.error("Error posting classified:", error);
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ padding: 4, borderRadius: 3, mt: 5, mb: 4 }}>
        <Typography
          variant="h5"
          textAlign="center"
          sx={{ fontWeight: "bold", mb: 3, color: "#42326e" }}
        >
          Post a Classified Ad
        </Typography>

        <Grid container spacing={2}>
          {/* Title field */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <CampaignIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <TextField
                fullWidth
                name="title"
                label="Title"
                value={newClassified.title}
                onChange={handleChange}
                error={!!errors.title}
                helperText={errors.title}
                variant="outlined"
              />
            </Box>
          </Grid>

          {/* Description field */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <DescriptionIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <TextField
                fullWidth
                name="description"
                label="Description"
                multiline
                rows={3}
                value={newClassified.description}
                onChange={handleChange}
                variant="outlined"
              />
            </Box>
          </Grid>

          {/* Price field */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <CurrencyRupeeIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <TextField
                fullWidth
                name="price"
                label="Price"
                type="number"
                value={newClassified.price}
                onChange={handleChange}
                error={!!errors.price}
                helperText={errors.price}
                variant="outlined"
              />
            </Box>
          </Grid>

          {/* City field */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <LocationCityIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <TextField
                fullWidth
                name="city"
                label="City"
                value={newClassified.city}
                onChange={handleChange}
                error={!!errors.city}
                helperText={errors.city}
                variant="outlined"
              />
            </Box>
          </Grid>

          {/* State field */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <PlaceIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <TextField
                fullWidth
                name="state"
                label="State"
                value={newClassified.state}
                onChange={handleChange}
                variant="outlined"
              />
            </Box>
          </Grid>

          {/* Country field */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <PublicIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <TextField
                fullWidth
                name="country"
                label="Country"
                value={newClassified.country}
                onChange={handleChange}
                variant="outlined"
              />
            </Box>
          </Grid>

          {/* Contact number field */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <PhoneIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <TextField
                fullWidth
                name="contact_number"
                label="Contact Number"
                value={newClassified.contact_number}
                onChange={handleChange}
                error={!!errors.contact_number}
                helperText={errors.contact_number}
                variant="outlined"
              />
            </Box>
          </Grid>

          {/* Email field */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <EmailIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <TextField
                fullWidth
                name="email"
                label="Email"
                value={newClassified.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                variant="outlined"
              />
            </Box>
          </Grid>

          {/* Images field */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <AddPhotoAlternateIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <TextField
                fullWidth
                name="images"
                label="Image URLs (comma separated)"
                value={newClassified.images}
                onChange={handleChange}
                variant="outlined"
                helperText="Enter multiple URLs separated by commas"
              />
            </Box>
          </Grid>

          {/* Category selection */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <CategoryIcon sx={{ flexShrink: 0, color: "#42326e" }} />
              <FormControl fullWidth error={!!errors.category} variant="outlined">
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={newClassified.category}
                  onChange={handleChange}
                  label="Category"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.category}</FormHelperText>
              </FormControl>
            </Box>
          </Grid>

          {/* Submit button */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handlePost}
              disabled={
                !newClassified.title ||
                !newClassified.price ||
                !newClassified.city ||
                !newClassified.category
              }
              sx={{
                padding: 1.5,
                fontSize: "1rem",
                fontWeight: "bold",
                borderRadius: 2,
                backgroundColor: "#42326e",
                color: "white",
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
            >
              Post Ad
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default PostClassified;
